/**
 * Toast Configuration
 *
 * Centralized configuration for toast notifications including:
 * - Default durations
 * - Position settings
 * - Behavior configuration
 *
 * Note: Visual styling is handled in components/ui/sonner.tsx using Tailwind classes
 */

/**
 * Toast options type
 * Based on sonner's toast API, but defined locally since it's not exported
 * Uses a more permissive type to match sonner's ExternalToast interface
 */
export type ToastOptions = {
  description?: string;
  duration?: number;
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  closeButton?: boolean;
  className?: string;
  id?: string | number;
  action?: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick?: () => void;
  };
  onDismiss?: (toast: unknown) => void;
  onAutoClose?: (toast: unknown) => void;
  // Additional properties that sonner might accept
  [key: string]: unknown;
};

/**
 * Toast type definitions
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

/**
 * Default toast configuration
 */
export const ToastConfig = {
  /**
   * Default duration for each toast type (in milliseconds)
   */
  duration: {
    success: 2000, // Brief - UI update is the main feedback
    error: 5000, // Longer - users need time to read errors
    warning: 4000,
    info: 2500,
    loading: Infinity,
  },

  /**
   * Position of toast notifications
   */
  position: 'top-right' as const,

  /**
   * Maximum number of toasts visible at once
   * Prevents toast pile-ups during rapid actions
   */
  maxVisible: 3,

  /**
   * Whether to show close button
   */
  closeButton: true,
} as const;

/**
 * Create toast options with defaults
 */
export function createToastOptions(overrides?: Partial<ToastOptions>): ToastOptions {
  return {
    duration: ToastConfig.duration.success,
    position: ToastConfig.position,
    closeButton: ToastConfig.closeButton,
    ...overrides,
  };
}

/**
 * Get duration for a specific toast type
 */
export function getToastDuration(type: ToastType): number {
  return ToastConfig.duration[type];
}


